import { getProviderContactPhone } from '@kyruus/provider-components';
import { PMCConfig, CustomCtaOption } from '@kyruus/types';
import { ExtendedProvider, Log } from 'Src/types';
import { DefaultCtaListItem, CUSTOM_LIST_ITEM_TRACKING_ID } from './constants';
import { looksLikeInternalBookingUrl } from 'Src/utils/url';

const shouldRenderBookOnline = (
  config: PMCConfig,
  provider: ExtendedProvider
): boolean => {
  const modalConfig = config.modal_display ?? {};
  const isBookOnlineItemEnabled = Boolean(modalConfig.book_online);
  const hasBookOnlineUrl = Boolean(provider.book_online_url);
  if (!isBookOnlineItemEnabled || !hasBookOnlineUrl) {
    return false;
  }
  const isExternalBookOnlineUrl = !looksLikeInternalBookingUrl(
    provider.book_online_url
  );
  // Do not consider availability for external booking
  if (isExternalBookOnlineUrl) {
    return true;
  }
  const providerHasAvailability = provider.has_provider_availability;
  if (providerHasAvailability) {
    return true;
  }
  return false;
};

type ListItemId = DefaultCtaListItem | CustomCtaOption['id'];
type OrderConfig = Array<ListItemId>;
interface GetOrderedListItemsProps {
  defaultListItems?: DefaultCtaListItem[];
  customListItems?: CustomCtaOption[];
  order: OrderConfig;
}

/**
 * Returns an array of CTA list items filtered to only include enabled items
 * and ordered according to the order config.
 * Items must be specified in order config to be included.
 * @param {Object} [GetOrderedListItemsProps] props - The parameters object.
 * @param {DefaultCtaListItem[]} [props.defaultListItems=[]] - The default CTA list items enabled for this provider.
 * @param {CustomCtaOption[]} [props.customListItems=[]] - The custom CTA list items from the config.
 * @param {OrderConfig} props.order - Array of list item ids in the order they should be displayed.
 * @returns {(DefaultCtaListItem | CustomCtaOption)[]} The ordered list of CTA items to render.
 */
export const getOrderedCtaListItems = ({
  defaultListItems = [],
  customListItems = [],
  order
}: GetOrderedListItemsProps): Array<DefaultCtaListItem | CustomCtaOption> => {
  // Flatten custom objects to just ids. Default items are already ids
  const customListItemIds = customListItems.map((item) => item.id);
  // Combine enabled default and custom ids into a set for easy lookup
  const enabledItems: Set<ListItemId> = new Set([
    ...defaultListItems,
    ...customListItemIds
  ]);
  // Remove any items from the order array that are not enabled for this provider
  const listItems: ListItemId[] = order.filter((item) =>
    enabledItems.has(item)
  );
  // Create a map of custom items for easy lookup
  const customListItemMap: Map<CustomCtaOption['id'], CustomCtaOption> =
    new Map(customListItems.map((item) => [item.id, item]));
  // Replace custom ids with objects from config, return default items as-is
  return listItems.map(
    (item) => customListItemMap.get(item) ?? (item as DefaultCtaListItem)
  );
};

export const getDefaultCtaListItems = (
  config: PMCConfig,
  provider: ExtendedProvider
): DefaultCtaListItem[] => {
  const listItemsToDisplay = [];
  const modalConfig = config.modal_display ?? {};

  if (shouldRenderBookOnline(config, provider)) {
    listItemsToDisplay.push(DefaultCtaListItem.BookOnline);
  }

  const phoneEnabled = modalConfig.provider_phone ?? false;
  const providerPhone = getProviderContactPhone(provider);
  if (phoneEnabled && providerPhone) {
    listItemsToDisplay.push(DefaultCtaListItem.Phone);
  }

  const requestFormEnabled = modalConfig.appointment_request_form ?? false;
  const providerHasRequestForm = Boolean(provider.request_appointment_url);
  if (requestFormEnabled && providerHasRequestForm) {
    listItemsToDisplay.push(DefaultCtaListItem.RequestForm);
  }

  const virtualCareEnabled = modalConfig.virtual_care ?? false;
  const providerHasVirtualCareUrl = Boolean(provider.virtual_care_url);
  if (providerHasVirtualCareUrl && virtualCareEnabled) {
    listItemsToDisplay.push(DefaultCtaListItem.VirtualCare);
  }

  return listItemsToDisplay;
};

interface GetCtaListItemsToRenderProps {
  config: PMCConfig;
  provider: ExtendedProvider;
}
export const getCtaListItemsToRender = ({
  config,
  provider
}: GetCtaListItemsToRenderProps): Array<
  DefaultCtaListItem | CustomCtaOption
> => {
  const defaultListItems = getDefaultCtaListItems(config, provider);
  const modalDisplay = config?.modal_display;
  const customListItems = modalDisplay?.custom_options ?? [];
  const order = modalDisplay?.order ?? [];
  if (!order.length) {
    return [...defaultListItems, ...customListItems];
  }
  return getOrderedCtaListItems({
    defaultListItems,
    customListItems,
    order
  });
};

export const getCustomListItemUrl = ({
  customOption,
  provider
}: {
  customOption: CustomCtaOption;
  provider: ExtendedProvider;
}): string | undefined => {
  if (customOption.provider_field) {
    const providerField = customOption.provider_field;
    // This will likely be a custom xp field
    const providerValue = provider[providerField as keyof ExtendedProvider];
    const providerUrl =
      typeof providerValue === 'string' ? providerValue : undefined;
    return providerUrl;
  }
  return customOption.url ?? undefined;
};

interface TrackListItemClickProps {
  listItem: DefaultCtaListItem | CustomCtaOption;
  providerId: string | number;
  primaryText: string;
  secondaryText?: string;
  log: Log;
}

export const trackListItemClick = ({
  listItem,
  log,
  providerId,
  primaryText,
  secondaryText
}: TrackListItemClickProps) => {
  const isDefaultListItem = typeof listItem === 'string';
  const eventId = isDefaultListItem ? listItem : CUSTOM_LIST_ITEM_TRACKING_ID;
  const listItemId = isDefaultListItem ? listItem : listItem.id;
  const eventData = {
    provider_id: providerId,
    cta_option_id: listItemId,
    cta_option_title: primaryText,
    cta_option_subtitle: secondaryText ?? null
  };
  log(`user_action.alter_view.request_appointment.${eventId}`, eventData);
};
